/**
 * The skin quiz.
 *
 * Scores every solution against the answers rather than branching through a decision
 * tree. A tree with eleven outcomes and four questions is unreadable and impossible for
 * anyone but its author to change; scoring keeps the tuning in the metaobject content,
 * where an advisor can edit it.
 *
 * Nothing is sent anywhere. Answers live in localStorage so a drop-off can resume.
 */

const STORAGE_KEY = 'sf-skin-quiz';

/** Weights. Symptoms dominate — they are the only thing the person actually told us
 *  about their skin, rather than a category they picked for themselves. */
const POINTS = {
  symptom: 10,
  skinType: 6,
  sensitivityPenalty: -14,
};

/**
 * One place to emit quiz analytics.
 *
 * Sends to gtag when it is on the page and pushes to dataLayer either way, so the events
 * survive a later move to Tag Manager. Silent when neither exists — the quiz is not going
 * to break because analytics did not load.
 *
 * Everything is prefixed `quiz_` so the funnel is one filter in GA4 rather than a hunt
 * through the standard events.
 */
function track(name, params = {}) {
  const payload = { ...params, quiz_version: 1 };
  try {
    if (typeof window.gtag === 'function') window.gtag('event', name, payload);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...payload });
  } catch {
    /* analytics must never take the quiz down */
  }
}

class SkinQuiz {
  #dialog;
  #solutions = [];
  #questions = [];
  #step = 0;
  #direction = 1;
  #leaving = false;
  #openedAt = 0;
  #completed = false;
  #entryPoint = 'unknown';
  #answers = { areas: [], symptoms: [], skinType: null, reactivity: null, experience: null };

  constructor(dialog) {
    this.#dialog = dialog;
    const raw = dialog.querySelector('[data-skin-quiz-data]');
    try {
      this.#solutions = JSON.parse(raw?.textContent || '[]');
    } catch {
      this.#solutions = [];
    }
    if (!this.#solutions.length) return;

    this.#questions = this.#buildQuestions();
    this.#restore();
    this.#bind();
  }

  /** The concerns question is assembled from the metaobjects, so adding a twelfth
   *  solution with symptoms puts it in the quiz with no code change. */
  #buildQuestions() {
    // Thirty-nine symptoms in one list is a scroll nobody finishes on a phone, and the
    // order looks arbitrary because it is just metaobject order. Ask for the area first
    // and the second question only carries what belongs to it.
    const areas = [];
    for (const s of this.#solutions) {
      const area = s.area || 'Other';
      if (!areas.includes(area)) areas.push(area);
    }

    return [
      {
        key: 'areas',
        multi: true,
        title: 'Where would you like to start?',
        hint: 'Pick one or more. We will only ask about what you choose.',
        options: areas.map((a) => ({ value: a, label: a, note: this.#areaNote(a) })),
      },
      {
        key: 'symptoms',
        multi: true,
        grouped: true,
        title: 'Which of these sound like you?',
        hint: 'Pick as many as apply — this is the answer that matters most.',
        options: () => this.#symptomOptions(),
      },
      {
        key: 'skinType',
        title: 'How does your skin behave most days?',
        options: [
          { value: 'oily', label: 'Oily', note: 'Shine across the day, especially the T-zone' },
          { value: 'dry', label: 'Dry', note: 'Tight, sometimes flaky' },
          { value: 'combination', label: 'Combination', note: 'Oily in places, dry in others' },
          { value: 'normal', label: 'Balanced', note: 'Neither reliably' },
        ],
      },
      {
        key: 'reactivity',
        title: 'How does your skin take new products?',
        hint: 'This decides how gently we start you.',
        options: [
          { value: 'never', label: 'Fine, almost always' },
          { value: 'sometimes', label: 'Occasionally something stings' },
          { value: 'easily', label: 'It reacts easily', note: 'Redness, stinging or breakouts from new products' },
        ],
      },
      {
        key: 'experience',
        title: 'What are you using now?',
        options: [
          { value: 'none', label: 'Not much', note: 'Cleanser, maybe a moisturiser' },
          { value: 'basics', label: 'The basics', note: 'Cleanser, moisturiser, SPF' },
          { value: 'actives', label: 'Actives already', note: 'Retinol, acids or vitamin C' },
          { value: 'clinical', label: 'Prescription or in-clinic', note: 'Tretinoin, or regular treatments' },
        ],
      },
    ];
  }

  /** A plain-language gloss so an area label is not the only thing to go on. */
  #areaNote(area) {
    const names = this.#solutions
      .filter((s) => (s.area || 'Other') === area)
      .map((s) => s.name);
    return names.join(', ');
  }

  #symptomOptions() {
    const chosen = this.#answers.areas;
    const inScope = this.#solutions.filter(
      (s) => !chosen.length || chosen.includes(s.area || 'Other')
    );
    const options = [];
    for (const solution of inScope) {
      for (const label of solution.symptoms || []) {
        options.push({ value: label, label, group: solution.name });
      }
    }
    return options;
  }

  #bind() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-skin-quiz-open]');
      if (trigger) {
        event.preventDefault();
        // Which button opened it. The point of adding entry points is knowing which ones
        // earn their place, and that is unanswerable without labelling them.
        this.#entryPoint = trigger.dataset.skinQuizSource || 'unknown';
        this.open();
      }
    });

    this.#dialog.querySelector('[data-skin-quiz-close]')?.addEventListener('click', () => this.close());
    this.#dialog.querySelector('[data-skin-quiz-back]')?.addEventListener('click', () => this.#advance(-1));
    this.#dialog.querySelector('[data-skin-quiz-next]')?.addEventListener('click', () => this.#advance(1));
    this.#dialog.querySelector('[data-skin-quiz-restart]')?.addEventListener('click', () => this.restart());

    // Clicking the backdrop closes it. `dialog` reports clicks on itself for the backdrop.
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.close();
    });
  }

  /** Opens the quiz and records what sent someone here. */
  openFrom(source) {
    this.#entryPoint = source;
    this.open();
  }

  open() {
    this.#openedAt = Date.now();
    track('quiz_opened', { quiz_entry_point: this.#entryPoint || 'unknown' });
    this.#render();
    if (typeof this.#dialog.showModal === 'function') this.#dialog.showModal();
    else this.#dialog.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
  }

  close() {
    // Abandonment is the number worth having: which step people leave on says what to cut.
    if (!this.#completed) {
      track('quiz_abandoned', {
        quiz_step: this.#step + 1,
        quiz_seconds: this.#openedAt ? Math.round((Date.now() - this.#openedAt) / 1000) : 0,
      });
    }
    if (typeof this.#dialog.close === 'function') this.#dialog.close();
    else this.#dialog.removeAttribute('open');
    document.body.style.overflow = '';
  }

  restart() {
    track('quiz_restarted', { quiz_step: this.#step + 1 });
    this.#completed = false;
    this.#answers = { areas: [], symptoms: [], skinType: null, reactivity: null, experience: null };
    this.#step = 0;
    this.#direction = 1;
    this.#leaving = false;
    this.#save();
    this.#render();
  }

  #restore() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (saved && typeof saved === 'object') {
        this.#answers = { ...this.#answers, ...saved.answers };
        this.#step = Math.min(saved.step || 0, this.#questions.length);
      }
    } catch {
      /* a corrupt entry should not stop the quiz opening */
    }
  }

  #save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ step: this.#step, answers: this.#answers }));
    } catch {
      /* private browsing */
    }
  }

  #go(direction) {
    const next = this.#step + direction;
    if (next < 0) return;
    this.#direction = direction;
    this.#step = Math.min(next, this.#questions.length);
    this.#save();
    this.#render();
    this.#dialog.querySelector('[data-skin-quiz-body]')?.scrollTo({ top: 0 });
  }

  #answered(question) {
    const value = this.#answers[question.key];
    return question.multi ? value.length > 0 : Boolean(value);
  }

  #render() {
    const body = this.#dialog.querySelector('[data-skin-quiz-body]');
    const back = this.#dialog.querySelector('[data-skin-quiz-back]');
    const next = this.#dialog.querySelector('[data-skin-quiz-next]');
    const progress = this.#dialog.querySelector('[data-skin-quiz-progress]');
    if (!body) return;

    const total = this.#questions.length;
    const done = this.#step >= total;
    progress?.style.setProperty('--skin-quiz-progress', `${Math.round((Math.min(this.#step, total) / total) * 100)}%`);
    back.hidden = this.#step === 0;
    const restart = this.#dialog.querySelector('[data-skin-quiz-restart]');
    if (restart) restart.hidden = this.#step === 0;

    if (done) {
      next.hidden = true;
      body.innerHTML = this.#resultsMarkup();
      this.#bindResultLinks();
      if (!this.#completed) {
        this.#completed = true;
        const top = this.#ranked().slice(0, 3);
        track('quiz_completed', {
          quiz_seconds: this.#openedAt ? Math.round((Date.now() - this.#openedAt) / 1000) : 0,
          quiz_entry_point: this.#entryPoint,
          quiz_top_result: top[0]?.name || 'none',
          quiz_results: top.map((r) => r.name).join(', '),
          quiz_skin_type: this.#answers.skinType || 'unset',
        });
      }
      return;
    }

    next.hidden = false;
    const question = this.#questions[this.#step];
    // Fires per step rather than once per question answered, so the funnel counts people
    // who reached a step, which is what a drop-off chart needs.
    track('quiz_step_viewed', { quiz_step: this.#step + 1, quiz_question: question.key });
    const options = typeof question.options === 'function' ? question.options() : question.options;
    next.disabled = !this.#answered(question);
    next.textContent = this.#step === total - 1 ? 'See my results' : 'Continue';

    const wide = options.length > 5 || options.some((o) => o.note);
    body.innerHTML = `
      <div class="skin-quiz__step${this.#direction < 0 ? ' skin-quiz__step--back' : ''}">
        <h2 class="skin-quiz__q">${escapeHtml(question.title)}</h2>
        ${question.hint ? `<p class="skin-quiz__hint">${escapeHtml(question.hint)}</p>` : ''}
        ${this.#optionsMarkup(question, options, wide)}
      </div>
    `;

    body.querySelectorAll('[data-value]').forEach((el) => {
      el.addEventListener('click', () => this.#choose(question, el.dataset.value, el));
    });
  }

  /** Grouped questions get a label per concern, so the list has an order you can see
   *  rather than metaobject order, which looks arbitrary. */
  #optionsMarkup(question, options, wide) {
    const cls = `skin-quiz__options${wide ? '' : ' skin-quiz__options--two'}`;
    if (!question.grouped) {
      return `<div class="${cls}" role="group">${
        options.map((o) => this.#optionMarkup(question, o)).join('')
      }</div>`;
    }

    const groups = [];
    for (const option of options) {
      let group = groups.find((g) => g.name === option.group);
      if (!group) groups.push((group = { name: option.group, items: [] }));
      group.items.push(option);
    }

    return groups.map((group) => `
      <p class="skin-quiz__group-label">${escapeHtml(group.name)}</p>
      <div class="${cls}" role="group">${
        group.items.map((o) => this.#optionMarkup(question, o)).join('')
      }</div>
    `).join('');
  }

  #optionMarkup(question, option) {
    const current = this.#answers[question.key];
    const selected = question.multi ? current.includes(option.value) : current === option.value;
    return `
      <button class="skin-quiz__opt" type="button" aria-pressed="${selected}" data-value="${escapeHtml(option.value)}">
        <span class="skin-quiz__mark" aria-hidden="true"></span>
        <span>
          <strong>${escapeHtml(option.label)}</strong>
          ${option.note ? `<br><span style="opacity:.72">${escapeHtml(option.note)}</span>` : ''}
        </span>
      </button>
    `;
  }

  #choose(question, value, button) {
    if (question.multi) {
      const list = this.#answers[question.key];
      const index = list.indexOf(value);
      if (index >= 0) list.splice(index, 1);
      else list.push(value);
    } else {
      this.#answers[question.key] = value;
    }
    this.#save();

    if (question.multi) {
      // Toggle in place rather than re-rendering. A full re-render replaces the step
      // markup, which restarts its entry animation — that was the flash on every tap.
      button?.setAttribute('aria-pressed', String(this.#answers[question.key].includes(value)));
      const next = this.#dialog.querySelector('[data-skin-quiz-next]');
      if (next) next.disabled = !this.#answered(question);
      return;
    }

    // Single-choice questions advance on their own — an extra tap per question is the
    // difference between finishing and abandoning on a phone. Mark the choice in place
    // first so the selection is visibly acknowledged, then leave. Re-rendering to show
    // the selection and re-rendering again to advance was two swaps back to back, which
    // is what made it feel like a blink rather than a step.
    this.#dialog.querySelectorAll('.skin-quiz__opt').forEach((el) => {
      el.setAttribute('aria-pressed', String(el === button));
    });
    this.#advance(1);
  }

  /** Plays the current step out before swapping in the next one. Without this the old
   *  markup is replaced mid-frame and the new step's entry animation starts from
   *  nothing, which reads as a flash. */
  #advance(direction) {
    if (this.#leaving) return;

    const step = this.#dialog.querySelector('.skin-quiz__step');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!step || reduced) {
      this.#go(direction);
      return;
    }

    this.#leaving = true;
    step.classList.add(direction < 0 ? 'skin-quiz__step--leaving-back' : 'skin-quiz__step--leaving');

    const finish = () => {
      this.#leaving = false;
      this.#go(direction);
    };
    // A timeout as well as the event: if the animation is interrupted — the dialog is
    // closed mid-step, say — animationend never fires and the quiz would wedge.
    let done = false;
    const once = () => { if (done) return; done = true; finish(); };
    step.addEventListener('animationend', once, { once: true });
    setTimeout(once, 260);
  }

  /** Score, then rank. Ties fall back to the metaobject's own weight. */
  #score() {
    const { symptoms, skinType, reactivity } = this.#answers;

    return this.#solutions
      .map((solution) => {
        let score = 0;
        const matched = (solution.symptoms || []).filter((s) => symptoms.includes(s));
        score += matched.length * POINTS.symptom;

        if (skinType && (solution.skinTypes || []).includes(skinType)) {
          score += POINTS.skinType;
        }

        // Reactive skin should not be pointed first at a routine built on strong actives.
        if (reactivity === 'easily' && solution.sensitivitySafe === false) {
          score += POINTS.sensitivityPenalty;
        }

        return { ...solution, score, matched: matched.length };
      })
      .sort((a, b) => b.score - a.score || b.weight - a.weight);
  }

  /** Concerns they named come first. If that is fewer than three, fill from what suits
   *  their skin type — related reading rather than a match, and labelled as such. */
  #ranked() {
    // Only concerns they actually named. Padding this with skin-type matches meant
    // showing people things they never said were a problem.
    return this.#score().filter((s) => s.matched > 0);
  }

  #bindResultLinks() {
    this.#dialog.querySelectorAll('.skin-quiz__card').forEach((link, index) => {
      link.addEventListener('click', () => {
        track('quiz_result_clicked', {
          quiz_destination: link.getAttribute('href') || '',
          quiz_result_position: index + 1,
          quiz_top_result: this.#ranked()[0]?.name || 'none',
        });
      });
    });
  }

  #resultsMarkup() {
    const results = this.#ranked();

    if (!results.length) {
      return `
        <div class="skin-quiz__step">
          <h2 class="skin-quiz__q">Nothing stood out</h2>
          <p class="skin-quiz__hint">You did not pick a concern, so there is nothing to match on.
          Browse the full set, or book a consultation and an advisor will work through it with you.</p>
          <div class="skin-quiz__result">
            <a class="skin-quiz__card" href="/pages/solutions-index"><h3>All solutions</h3>
            <p>Eleven concerns, each with the routine our advisors build for it.</p></a>
          </div>
        </div>
      `;
    }

    // One recommendation, full stop. Everything else they flagged is still a concern,
    // but listing it here only asked them to choose again at the moment they had just
    // been given an answer.
    const [primary] = results;

    return `
      <div class="skin-quiz__step">
        <h2 class="skin-quiz__q">Start with ${escapeHtml(primary.name)}</h2>
        <p class="skin-quiz__hint">${escapeHtml(this.#advice())}</p>

        <div class="skin-quiz__result">
          <a class="skin-quiz__card skin-quiz__card--top skin-quiz__card--media" href="${escapeHtml(primary.url)}">
            ${primary.image
              ? `<img class="skin-quiz__card-img" src="${escapeHtml(primary.image)}" alt="" loading="lazy">`
              : '<span></span>'}
            <span class="skin-quiz__card-body">
              <span class="skin-quiz__rank">Your routine</span>
              <h3>${escapeHtml(primary.name)}</h3>
              <p>${escapeHtml(primary.intro || '')}</p>
              <span class="skin-quiz__go">See the routine</span>
            </span>
          </a>
        </div>

      </div>
    `;
  }

  /** One line of pacing advice, from reactivity and what they already use. */
  #advice() {
    const { reactivity, experience } = this.#answers;
    if (reactivity === 'easily') {
      return 'Because your skin reacts easily, introduce one product at a time and give each two weeks before adding the next.';
    }
    if (experience === 'none') {
      return 'Start with the cleanser, moisturiser and SPF from this routine, then add the treatment step once those are habit.';
    }
    if (experience === 'clinical') {
      return 'You are already using actives, so check with your clinician before layering anything new alongside them.';
    }
    return 'Add one product at a time and give each a couple of weeks — that way you know what is working.';
  }
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

function init() {
  const quizzes = [];
  document.querySelectorAll('[data-skin-quiz]').forEach((dialog) => {
    if (dialog.dataset.skinQuizReady) return;
    dialog.dataset.skinQuizReady = 'true';
    quizzes.push(new SkinQuiz(dialog));
  });

  // The quiz is a dialog, so it had no address — nothing to put in an email, an SMS or a
  // social post. `?quiz=open` on any page of the store opens it on load, which gives it a
  // linkable URL without turning it into a page that would then rank against the solution
  // pages it is meant to feed.
  //
  // The entry point is read from the link so campaigns stay separable: `?quiz=open&from=
  // welcome-email` shows up in GA4 as its own source rather than merging into one bucket.
  const params = new URLSearchParams(window.location.search);
  if (params.get('quiz') === 'open' && quizzes.length) {
    const source = params.get('from') || 'link';
    quizzes[0].openFrom(`link:${source}`);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
